import { SearchOutlined } from '@ant-design/icons';
import { Input, Spin } from 'antd';
import { debounce } from 'lodash';
import { FC, useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { IPatient, useSearchPatientsQuery } from 'services/patientsApi';
import { useOnClickOutside } from 'hooks/useOnClickOutside';
import { CASE_TYPE_TO_UI_CONVERTER } from 'config/constants';
import { toogleBodyElmScroll } from 'utils/ui';

type TSearchOption = TOptionString & {
  href: string;
};

const DIV = styled.div`
  line-height: 24px;
  cursor: pointer;
  font-weight: 600;

  &:hover {
    background-color: #efefef;
  }
`;

type TOptionProps = {
  label: string;
  onClick: () => void;
};

const Option: FC<TOptionProps> = ({ label, onClick }) => (
  <DIV onClick={onClick} className="px-3 py-2">
    <SearchOutlined className="mr-2" />
    {label}
  </DIV>
);

export const PatientSearch: FC = () => {
  const [input, setInput] = useState<string>('');
  const [searchValue, setSearchValue] = useState<string>('');

  const boxRef = useRef<HTMLDivElement>(null);
  const [isBoxOpen, setIsBoxOpen] = useState<boolean>(false);
  useOnClickOutside(boxRef, () => setIsBoxOpen(false));

  const { searchedPatients, isSearchingPatients, isFetchingPatients } = useSearchPatientsQuery(searchValue, {
    skip: searchValue.length < 3,
    selectFromResult: ({ data, isLoading, isFetching, isUninitialized }) => ({
      searchedPatients: data?.cases.map((item: IPatient) => ({
        label: item.patient.fullName,
        value: item.id,
        href:
          item.submissionStatus === 'draft'
            ? `/dentist/patients/submit/${CASE_TYPE_TO_UI_CONVERTER[item.serviceType]}?caseId=${item.id}`
            : `/dentist/patients/${item.id}`,
      })),
      isSearchingPatients: isLoading,
      isFetchingPatients: isFetching,
      isUninitializedPatients: isUninitialized,
    }),
  });

  const handleChange = (value: string) => {
    setInput(value);
  };

  const setSearchValueDebounce = useCallback(
    debounce((value) => setSearchValue(value), 500),
    [],
  );

  useEffect(() => {
    setSearchValueDebounce(input);
  }, [input, setSearchValueDebounce]);

  const handleClick = (option: TSearchOption) => {
    window.location.href = option.href;
  };

  return (
    <div
      className="relative"
      ref={boxRef}
      onMouseOver={(_e) => toogleBodyElmScroll(false)}
      onMouseOut={(_e) => toogleBodyElmScroll(true)}
      onBlur={() => undefined}
      onFocus={() => undefined}
    >
      <Input
        placeholder="Search for a patient"
        value={input}
        onChange={(e) => handleChange(e.target.value)}
        style={{ width: 300 }}
        onFocus={() => setIsBoxOpen(true)}
      />
      {isBoxOpen && (
        <div
          className="absolute custom-shadow-2 b-white overflow-y-scroll radius-5"
          style={{ width: 300, maxHeight: 400 }}
        >
          {input.length < 3 ? (
            <div className="text-center">Please input at least 3 letters</div>
          ) : isSearchingPatients || isFetchingPatients ? (
            <div className="flex justify-content-center align-items-center" style={{ minHeight: '100px' }}>
              <Spin size="small" tip="Searching patient..." />
            </div>
          ) : searchedPatients && searchedPatients.length ? (
            searchedPatients.map((patient) => <Option label={patient.label} onClick={() => handleClick(patient)} />)
          ) : (
            <div className="text-center">No patient found</div>
          )}
        </div>
      )}
    </div>
  );
};
